/**
 * Workspace Recent Chat Item Component
 *
 * Displays a single recent chat session from workspace sidebar
 * Similar to session-list-item but includes agent information
 * Shows chat name, agent name, and formatted timestamp
 */

import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '../../shared/components/icons';
import { ChatNameEditorComponent } from '../workspace/workspace-recent-chats/components/chat-name-editor/chat-name-editor.component';
import { RecentChat } from '../../api/models/session.model';

@Component({
  selector: 'app-workspace-recent-chat-item',
  standalone: true,
  imports: [
    MatTooltipModule,
    TranslatePipe,
    IconComponent,
    ChatNameEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './workspace-recent-chat-item.component.html',
  styleUrls: ['./workspace-recent-chat-item.component.scss'],
})
export class WorkspaceRecentChatItemComponent {
  private readonly translate = inject(TranslateService);

  /** Recent chat data */
  chat = input.required<RecentChat>();

  /** Whether to show timestamp (default: false when using group headers) */
  showTimestamp = input<boolean>(false);

  /** Chat click event */
  chatClick = output<RecentChat>();

  /** Chat delete event */
  chatDelete = output<RecentChat>();

  /** Chat rename event - emits {chat, newName} */
  chatRename = output<{ chat: RecentChat; newName: string }>();

  /** Editing state */
  isEditing = signal<boolean>(false);

  /** Editing name value */
  editingName = signal<string>('');

  startEditing(event: Event): void {
    event.stopPropagation();
    const currentName = this.getChatDisplayName();
    this.editingName.set(currentName);
    this.isEditing.set(true);
  }

  saveEdit(newName: string): void {
    this.chatRename.emit({
      chat: this.chat(),
      newName,
    });
    this.isEditing.set(false);
  }

  cancelEdit(): void {
    this.editingName.set('');
    this.isEditing.set(false);
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    this.chatDelete.emit(this.chat());
  }

  /**
   * Get aria-label for chat button
   */
  getAriaLabel(): string {
    const chatName = this.getChatDisplayName();
    const agentName = this.chat().agentName;
    const timestamp = this.formatTimestamp();
    return this.translate.instant('WORKSPACE.RECENT_CHATS.A11Y.OPEN_CHAT', {
      chatName,
      agentName,
      timestamp,
    });
  }

  /**
   * Get display name for chat session
   * Returns session name or "Untitled Chat" if null
   */
  getChatDisplayName(): string {
    return (
      this.chat().sessionName ||
      this.translate.instant('WORKSPACE.RECENT_CHATS.UNTITLED')
    );
  }

  /**
   * Format timestamp for display
   * Shows "Today", "Yesterday", or date
   */
  formatTimestamp(): string {
    const date = new Date(this.chat().lastMessageTimestamp);
    const now = new Date();

    // Reset hours to compare dates only
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const todayOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );

    const diffMs = todayOnly.getTime() - dateOnly.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return this.translate.instant('SESSION.GROUP_TODAY');
    }
    if (diffDays === 1) {
      return this.translate.instant('SESSION.GROUP_YESTERDAY');
    }
    if (diffDays < 7) {
      return this.translate.instant('SESSION.DAYS_AGO', { days: diffDays });
    }

    // Format as short date (e.g., "Mar 15")
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }
}
